// src/components/AboutConsultationSection.jsx
import React from "react";

/**
 * Improved About section rendering:
 * - Parses aboutText into paragraphs and lists (lines starting with •, - or *)
 * - Uses textAlign: 'justify' (not whitespace-pre-line) to avoid awkward line breaks
 * - Keeps responsive paddings (p-4 md:p-8) as previously tuned
 */

export default function AboutConsultationSection({
    photo = "/assets/images/rashmi.jpg",
    brandName = "Gantavyam",
}) {
    const content = {
        founderName: "Rashmi Modi, CFA",
        founderTitle: "Founder, Gantavyam",
        heading: "Gantavyam",
        aboutText: `Gantavyam specializes in the recovery of old, forgotten, or unclaimed shares—including physical shares, shares transferred to IEPF, and lost dividends. Our expert team simplifies every step, from locating lost investments to handling complex documentation and legal processes for transmission, transfer, and dematerialization.

Clients trust our proven track record and industry knowledge to reclaim their rightful assets efficiently and securely. Services include:
• Recovery of shares and dividends transferred to IEPF
• Transmission and transfer of shares after succession
• Dematerialization of physical and torn certificates
• Handling signature and name mismatches during transfer
• Search and trace of forgotten investments and lost properties

Experience peace of mind as our professionals manage the entire recovery process, keeping you informed and protected at every stage. Let us help you unlock hidden wealth and legacy.`,
        services: [
            "Recovery of shares and dividends transferred to IEPF",
            "Transmission and transfer of shares after succession",
            "Dematerialization of physical and torn certificates",
            "Handling signature and name mismatches during transfer",
            "Search and trace of forgotten investments and lost properties",
        ],
        linkedIn: "https://www.linkedin.com/in/rashmimodicfa/",
        licenseCode: "ARN - 156455",
        stats: [
            { value: "₹20+ Cr", label: "Recovered" },
            { value: "100+", label: "Companies Dealt" },
            { value: "50+", label: "Clients Served" },
        ],
    };

    // Utility: parse the aboutText into blocks (paragraphs or lists)
    function parseTextToBlocks(text) {
        const lines = text.split(/\r?\n/);
        const blocks = [];
        let currentParagraphLines = [];
        let currentListItems = null;

        function flushParagraph() {
            if (currentParagraphLines.length) {
                blocks.push({ type: "p", text: currentParagraphLines.join(" ").trim() });
                currentParagraphLines = [];
            }
        }

        function flushList() {
            if (currentListItems && currentListItems.length) {
                blocks.push({ type: "ul", items: currentListItems });
                currentListItems = null;
            }
        }

        for (let rawLine of lines) {
            const line = rawLine.trim();

            if (!line) {
                // blank line => break between blocks
                flushParagraph();
                flushList();
                continue;
            }

            // list item if starts with bullet char (•, -, or *)
            const listMatch = line.match(/^[•\-\*]\s*(.*)/);
            if (listMatch) {
                // start list if needed
                if (!currentListItems) {
                    flushParagraph();
                    currentListItems = [];
                }
                currentListItems.push(listMatch[1].trim());
                continue;
            }

            // Also detect lines like "Services include:" followed by indented bullets:
            // If line ends with ":" we treat as paragraph then next lines may be bullets.
            // For normal text lines, accumulate into paragraph (merge short lines).
            currentParagraphLines.push(line);
        }

        // flush any remaining
        flushParagraph();
        flushList();

        return blocks;
    }

    const blocks = parseTextToBlocks(content.aboutText);

    return (
        <section
            id="about"
            className="max-w-6xl px-4 md:px-12 lg:px-20 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
        >
            {/* ABOUT (primary) */}
            <div className="lg:col-span-7 bg-white rounded-2xl p-4 md:p-8 shadow-sm">
                <div className="max-w-[740px]">
                    <h3
                        className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight mb-4"
                    >
                        <span style={{ color: "#0f1724" }}>About</span>{" "}
                        <span style={{ color: "#e96a1e" }}>Gantavyam</span>
                    </h3>

                    {/* Render parsed blocks: paragraphs and lists */}
                    <div className="text-base text-slate-700 leading-relaxed">
                        {blocks.map((blk, i) => {
                            if (blk.type === "p") {
                                return (
                                    <p
                                        key={i}
                                        style={{ textAlign: "justify", marginBottom: "0.875rem", lineHeight: 1.6 }}
                                        // keep whitespace-normal so lines wrap normally
                                        className=""
                                    >
                                        {blk.text}
                                    </p>
                                );
                            } else if (blk.type === "ul") {
                                return (
                                    <ul
                                        key={i}
                                        className="list-inside list-disc text-sm text-slate-600 mb-4"
                                        style={{ marginLeft: "1rem" }}
                                    >
                                        {blk.items.map((it, idx) => (
                                            <li key={idx} style={{ marginBottom: "0.2rem", lineHeight: 1.5 }}>
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            }
                            return null;
                        })}
                    </div>

                    {/* Services list (kept as explicit list below if you want duplication removed, you can remove this block) */}
                    <h4 className="text-lg font-medium text-[#0f1724] mt-4 mb-3">Services</h4>
                    <ul className="list-inside list-disc text-sm text-slate-600 space-y-1">
                        {content.services.map((s, i) => (
                            <li key={i}>{s}</li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* FOUNDER (secondary) */}
            <aside className="lg:col-span-5 self-center">
                <div className="rounded-2xl p-3 md:p-8 shadow-sm bg-white border border-[rgba(15,23,36,0.05)] text-center">
                    <img
                        src={photo}
                        alt={`${content.founderName} ${content.founderTitle}`}
                        className="w-36 h-36 rounded-full object-cover mx-auto shadow-md border border-[rgba(26,30,46,0.08)]"
                    />

                    <div className="mt-4">
                        <p className="text-lg font-semibold text-[var(--accent,#A47C35)]">
                            {content.founderName}
                        </p>
                        <p className="text-sm text-slate-600">{content.founderTitle}</p>
                    </div>

                    <div className="mt-6 text-sm text-slate-700 leading-relaxed">
                        <p style={{ textAlign: "justify" }}>
                            {brandName} was founded by {content.founderName}. With deep expertise in share recovery, she guides clients through the entire process with transparency and care.
                        </p>
                    </div>

                    <div className="mt-5 flex flex-col items-center gap-2 text-sm text-slate-600">
                        <a
                            href={content.linkedIn}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 hover:opacity-90 transition"
                            aria-label="Rashmi Modi on LinkedIn"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-[#0A66C2]" aria-hidden="true">
                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764c.967 0 1.75.79 1.75 1.764s-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-1.337-.027-3.059-1.865-3.059-1.867 0-2.153 1.459-2.153 2.968v5.695h-3v-11h2.879v1.507h.041c.401-.758 1.379-1.559 2.84-1.559 3.04 0 3.605 2.003 3.605 4.609v6.443z" />
                            </svg>
                            <span className="text-sm">Connect on LinkedIn</span>
                        </a>

                        <div className="text-xs text-slate-500">
                            <span className="font-medium">License code</span> {content.licenseCode}
                        </div>
                    </div>
                </div>
            </aside>
        </section>
    );
}
